import { z } from 'zod/v4';
import { StyleDocumentAiResultStatusSchema } from './status.js';

/** Jsonb payload stored in `ai_document_type`. Both fields optional — the
 *  AI worker skips one or both when it can't classify. */
export const AiDocumentTypeSchema = z.object({
  documentType: z.string().optional(),
  documentTypeConfidence: z.number().min(0).max(1).optional(),
});
export type AiDocumentTypeType = z.infer<typeof AiDocumentTypeSchema>;

/** A single entry inside `ai_styles` — the AI worker's per-style match
 *  metadata. `styleName` is denormalised (worker emits it) so the review UI
 *  can render the human name without an extra lookup. `codes` is optional and
 *  is populated at API-response time by the pending-review route (the AI
 *  worker itself does not write codes into `ai_styles`). */
export const AiStyleSchema = z.object({
  styleId: z.string(),
  styleName: z.string().optional(),
  matchSource: z.string().optional(),
  matchContent: z.string().optional(),
  matchConfidence: z.number().optional(),
  comment: z.string().optional(),
  codes: z
    .array(z.object({ codeId: z.string(), codeValue: z.string() }))
    .optional(),
});
export type AiStyleType = z.infer<typeof AiStyleSchema>;

/** A stored row from `style_document_ai_results`. Loose typing on
 *  `userStyles` for now — its exact shape is expected to evolve as the
 *  review write path takes shape. */
export const StyleDocumentAiResultSchema = z.object({
  id: z.string(),
  styleDocumentAiRequestId: z.string(),
  fileId: z.string(),
  fileName: z.string(),
  /** Read-time-signed URL — derived from the joined `files` row's bucket +
   *  object key by `StyleDocumentAiResultsService.listPendingReview`. Never
   *  persisted; expiry is bounded by the response TTL of the enclosing HTTP
   *  request. */
  fileUrl: z.string(),
  styleMatch: z.boolean(),
  status: StyleDocumentAiResultStatusSchema,
  aiDocumentType: AiDocumentTypeSchema.nullable(),
  aiStyles: z.array(AiStyleSchema),
  userDocumentType: z.string().nullable(),
  userStyles: z.array(z.unknown()).nullable(),
  createdAt: z.coerce.date().nullable(),
  updatedAt: z.coerce.date().nullable(),
});

export type StyleDocumentAiResultType = z.infer<
  typeof StyleDocumentAiResultSchema
>;
